import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsEvents, analytics } from "common/analytics";
import DetailDialog from "common/components/DetailDialog";
import { useTranslation } from "common/locales";
import type { Geometry } from "geojson";
import { ARABLE_TYPE, FIELD_TYPE, GRASS_TYPE } from "model";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  SBIFieldImportResult,
  SBIFieldImportSelection,
  SBIPreviewResponse,
} from "services/sbiImportOperations";
import {
  importSBIToExistingFarm,
  previewSBIFields,
} from "services/sbiImportOperations";
import { useAppStore } from "stores";
import contactSupportImage from "../../assets/images/contact-support.png";
import manualDrawImage from "../../assets/images/manual-draw.png";
import SBIImportMapPreview from "./SBIImportMapPreview";

const GreenCard = styled(Box)(({ theme }) => ({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  alignItems: "center",
  textAlign: "center",
  width: "100%",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5),
  width: "100%",
  fontWeight: 500,
}));

const DigitInput = styled(TextField)(({ theme }) => ({
  width: "2em",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: 0,
    marginLeft: "-1px",
    "& input": {
      textAlign: "center",
      padding: theme.spacing(1),
      fontSize: "1.5rem",
      width: "100%",
      height: "1.5em",
    },
    "&:hover, &.Mui-focused": {
      zIndex: 1,
    },
  },
}));

const FirstDigitInput = styled(DigitInput)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    marginLeft: 0,
  },
}));

const LastDigitInput = styled(DigitInput)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
}));

const DigitsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
}));

const SBILookupRow = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  justifyContent: "center",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    alignItems: "stretch",
    flexDirection: "column",
  },
}));

const OptionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(2),
  textAlign: "left",
  backgroundColor: "white",
  color: "black",
  borderRadius: 12,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "auto",
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(2),
  },
  "& img": {
    width: 56,
    height: 56,
    borderRadius: 8,
  },
}));

const OptionDescription = styled(Typography)(({ theme }) => ({
  color: "black",
  marginTop: theme.spacing(0.5),
  textTransform: "none",
  fontSize: "0.875rem",
}));

type ImportStep = "entry" | "checking" | "preview" | "importing" | "success";

type EditableSBIField = {
  sourceIndex: number;
  include: boolean;
  name: string;
  fieldType: FIELD_TYPE;
  grassType: GRASS_TYPE;
  geometry: Geometry;
  arableType: ARABLE_TYPE;
};

type SBIFieldImportResultView = Omit<SBIFieldImportResult, "status"> & {
  status: SBIFieldImportResult["status"] | "pending";
};

interface NoFieldsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function NoFieldsModal({ open, onClose }: NoFieldsModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ImportStep>("entry");
  const [error, setError] = useState<string | null>(null);
  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const locale = useAppStore((state) => state.locale);
  const [digits, setDigits] = useState(Array(9).fill("")); // Changed to 9 digits
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showManualOptions, setShowManualOptions] = useState(false);
  const [preview, setPreview] = useState<SBIPreviewResponse | null>(null);
  const [editableFields, setEditableFields] = useState<EditableSBIField[]>([]);
  const [activeSourceIndex, setActiveSourceIndex] = useState<
    number | undefined
  >(undefined);
  const [fieldsCreated, setFieldsCreated] = useState<number | null>(null);
  const [fieldImportResults, setFieldImportResults] = useState<
    SBIFieldImportResultView[]
  >([]);

  const sbiNumber = digits.join("");
  const canUseSBIImport = locale.toLowerCase().startsWith("en");
  const isSbiComplete = /^\d{9}$/.test(sbiNumber);
  const isWorking = step === "checking" || step === "importing";
  const selectedFieldsCount = useMemo(
    () => editableFields.filter((field) => field.include).length,
    [editableFields]
  );
  const activeField = useMemo(
    () =>
      editableFields.find((field) => field.sourceIndex === activeSourceIndex) ??
      editableFields[0],
    [activeSourceIndex, editableFields]
  );
  const hasInvalidSelectedFields = useMemo(
    () =>
      editableFields.some(
        (field) =>
          field.include &&
          (!field.name.trim() ||
            (field.fieldType === FIELD_TYPE.GRASS && !field.grassType) ||
            (field.fieldType === FIELD_TYPE.ARABLE && !field.arableType))
      ),
    [editableFields]
  );
  const canImportSelectedFields =
    selectedFieldsCount > 0 && !hasInvalidSelectedFields;

  const buildEditableFields = (
    nextPreview: SBIPreviewResponse
  ): EditableSBIField[] =>
    nextPreview.fields.map((field) => ({
      sourceIndex: field.sourceIndex,
      include: true,
      name: field.name,
      fieldType:
        field.fieldType === "GRASS" ? FIELD_TYPE.GRASS : FIELD_TYPE.ARABLE,
      grassType: GRASS_TYPE.PERMANENT_PASTURE,
      geometry: field.geometry,
      arableType: ARABLE_TYPE.BARLEY,
    }));

  const resetImportState = () => {
    setStep("entry");
    setError(null);
    setPreview(null);
    setEditableFields([]);
    setActiveSourceIndex(undefined);
    setFieldsCreated(null);
    setFieldImportResults([]);
  };

  const handleClose = () => {
    resetImportState();
    setShowManualOptions(false);
    onClose();
  };

  const handleCheckSBI = async () => {
    if (!isSbiComplete) {
      setError(t("noFieldsModal.sbi.validation"));
      return;
    }

    setStep("checking");
    setError(null);
    setPreview(null);
    setEditableFields([]);
    setActiveSourceIndex(undefined);
    setFieldImportResults([]);

    try {
      const nextPreview = await previewSBIFields(sbiNumber);
      setPreview(nextPreview);
      const nextFields = buildEditableFields(nextPreview);
      setEditableFields(nextFields);
      setActiveSourceIndex(nextFields[0]?.sourceIndex);
      setStep("preview");
    } catch (error) {
      setStep("entry");
      setError(t("noFieldsModal.preview.error"));
      console.error("Failed to preview SBI:", error);
    }
  };

  const handleImportWithSBI = async () => {
    if (!currentFarmId) {
      setError("No farm selected");
      return;
    }

    if (!preview) {
      await handleCheckSBI();
      return;
    }

    if (!canImportSelectedFields) {
      setError(t("noFieldsModal.selection.error"));
      return;
    }

    const fieldsToImport: SBIFieldImportSelection[] = editableFields.map(
      (field) => ({
        sourceIndex: field.sourceIndex,
        include: field.include,
        name: field.name.trim(),
        fieldType: field.fieldType,
        grassType:
          field.include && field.fieldType === FIELD_TYPE.GRASS
            ? field.grassType
            : undefined,
        arableType:
          field.include && field.fieldType === FIELD_TYPE.ARABLE
            ? field.arableType
            : undefined,
      })
    );
    const pendingFieldResults: SBIFieldImportResultView[] = editableFields.map(
      (field) => ({
        sourceIndex: field.sourceIndex,
        name: field.name.trim() || t("noFieldsModal.selection.unnamedField"),
        status: field.include ? "pending" : "not_imported",
        reason: field.include
          ? undefined
          : t("noFieldsModal.import.result.notSelected"),
      })
    );

    setStep("importing");
    setError(null);
    setFieldImportResults(pendingFieldResults);
    analytics.track(AnalyticsEvents.SBI_IMPORT_STARTED);
    try {
      const response = await importSBIToExistingFarm(
        { sbiNumber, farmGuid: currentFarmId, fields: fieldsToImport },
        queryClient
      );

      setFieldsCreated(response.fieldsCreated);
      setFieldImportResults(response.fieldResults ?? pendingFieldResults);
      analytics.track(AnalyticsEvents.SBI_IMPORT_COMPLETED);
      setStep("success");
    } catch (error) {
      setStep("preview");
      setFieldImportResults([]);
      analytics.track(AnalyticsEvents.SBI_IMPORT_FAILED);
      setError(t("noFieldsModal.import.error"));
      console.error("Failed to import SBI:", error);
    }
  };

  const handleDrawManually = () => {
    handleClose();
    navigate("/add-field/enter-details");
  };

  const handleContactSupport = () => {
    // TODO: Implement support contact functionality
    window.location.href = "mailto:support@ruumi.io";
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    if (value && !/^\d$/.test(value)) return; // Only allow numbers

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError(null);
    setPreview(null);
    setEditableFields([]);
    setActiveSourceIndex(undefined);
    setFieldsCreated(null);
    setFieldImportResults([]);
    setStep("entry");

    // Move to next input if value was entered
    if (value && index < 8) {
      // Changed to 8 (last index)
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Extract only numeric digits from pasted content
    const numericDigits = pastedData.replace(/\D/g, "");

    if (numericDigits.length === 0) return;

    // Create new digits array
    const newDigits = Array(9).fill("");

    // Fill in the digits from pasted content, up to the maximum length
    for (let i = 0; i < Math.min(numericDigits.length, 9); i++) {
      newDigits[i] = numericDigits[i];
    }

    setDigits(newDigits);
    setError(null);
    setPreview(null);
    setEditableFields([]);
    setActiveSourceIndex(undefined);
    setFieldsCreated(null);
    setFieldImportResults([]);
    setStep("entry");

    // Focus the next empty input or the last filled input
    const nextEmptyIndex = newDigits.findIndex((digit) => digit === "");
    const focusIndex =
      nextEmptyIndex !== -1
        ? nextEmptyIndex
        : Math.min(numericDigits.length - 1, 8);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleViewFields = () => {
    if (currentFarmId) {
      handleClose();
      navigate(`/farm/${currentFarmId}`, { replace: true });
    }
  };

  const updateEditableField = (
    sourceIndex: number,
    updates: Partial<EditableSBIField>
  ) => {
    setEditableFields((fields) =>
      fields.map((field) =>
        field.sourceIndex === sourceIndex ? { ...field, ...updates } : field
      )
    );
    setError(null);
  };

  const handleFieldTypeChange = (
    sourceIndex: number,
    fieldType: FIELD_TYPE
  ) => {
    updateEditableField(sourceIndex, { fieldType });
  };

  const setAllFieldsIncluded = (include: boolean) => {
    setEditableFields((fields) =>
      fields.map((field) => ({
        ...field,
        include,
      }))
    );
    setError(null);
  };

  const getFieldKindLabel = (field: EditableSBIField) =>
    field.fieldType === FIELD_TYPE.GRASS
      ? t("fieldinput.fieldType.option.GRASS")
      : t("fieldinput.fieldType.option.ARABLE");

  const getFieldSubtypeLabel = (field: EditableSBIField) =>
    field.fieldType === FIELD_TYPE.GRASS
      ? t(`fieldinput.grassType.option.${field.grassType}`)
      : t(`fieldinput.arableType.option.${field.arableType}`);

  const importedResultCount = fieldImportResults.filter(
    (result) => result.status === "imported"
  ).length;
  const notImportedResultCount = fieldImportResults.filter(
    (result) => result.status === "not_imported"
  ).length;
  const renderImportResultList = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxHeight: { xs: "min(44vh, 360px)", sm: 360 },
        overflowY: "auto",
        pr: 0.5,
        width: "100%",
      }}
    >
      {fieldImportResults.map((result) => {
        const isImported = result.status === "imported";
        const isNotImported = result.status === "not_imported";

        return (
          <Box
            key={result.sourceIndex}
            sx={{
              alignItems: "flex-start",
              backgroundColor: "white",
              border: "1px solid",
              borderColor: isImported
                ? "success.light"
                : isNotImported
                ? "error.light"
                : "divider",
              borderRadius: 1.5,
              display: "grid",
              gap: 1,
              gridTemplateColumns: "auto minmax(0, 1fr) auto",
              p: 1.25,
              textAlign: "left",
            }}
          >
            {result.status === "pending" ? (
              <CircularProgress size={18} sx={{ mt: 0.25 }} />
            ) : isImported ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <ErrorOutlineIcon color="error" fontSize="small" />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {result.name}
              </Typography>
              {result.reason && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ whiteSpace: "normal" }}
                >
                  {result.reason}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              color={
                isImported ? "success" : isNotImported ? "error" : "default"
              }
              label={
                result.status === "pending"
                  ? t("noFieldsModal.import.result.pending")
                  : isImported
                  ? t("noFieldsModal.import.result.imported")
                  : t("noFieldsModal.import.result.notImported")
              }
              variant={isImported ? "filled" : "outlined"}
            />
          </Box>
        );
      })}
    </Box>
  );

  const dialogTitle =
    step === "success" ? (
      t("noFieldsModal.success.title")
    ) : preview && (step === "preview" || step === "importing") ? (
      <>
        {t("noFieldsModal.preview.title")}
        <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
          {" · "}
          {t("noFieldsModal.preview.sbiNumber", { sbiNumber })}
        </Box>
      </>
    ) : (
      t("noFieldsModal.title")
    );
  const isCompactDialog =
    !preview || step === "importing" || step === "success";
  const showPreviewActions =
    Boolean(preview) && step === "preview" && !showManualOptions;
  const showEntryManualOptions = !preview && step === "entry";
  const showSBIEntry = showEntryManualOptions && canUseSBIImport;
  const showSBIEntryError = showSBIEntry && Boolean(error);
  const headerActions = showPreviewActions ? (
    <>
      <Button
        color="primary"
        disabled={isWorking}
        onClick={() => setShowManualOptions(true)}
        sx={{ display: { xs: "none", sm: "inline-flex" } }}
        variant="text"
      >
        {t("noFieldsModal.manual.button")}
      </Button>
      <Button
        color="primary"
        disabled={isWorking || !canImportSelectedFields}
        onClick={handleImportWithSBI}
        sx={{
          backgroundColor: "#2E7D32",
          borderRadius: 2,
          display: { xs: "none", sm: "inline-flex" },
          px: 2.5,
          whiteSpace: "nowrap",
        }}
        variant="contained"
      >
        {isWorking ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          t("noFieldsModal.import.buttonWithCount", {
            count: selectedFieldsCount,
          })
        )}
      </Button>
    </>
  ) : undefined;

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("event-form-dialog", {
        detail: { open: showPreviewActions },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("event-form-dialog", { detail: { open: false } })
      );
    };
  }, [showPreviewActions]);

  if (showManualOptions) {
    return (
      <DetailDialog
        open={open}
        onClose={handleClose}
        onBack={() => setShowManualOptions(false)}
        title={t("noFieldsModal.manual.title")}
        contentSx={{ backgroundColor: "#F7FCF1" }}
      >
        <GreenCard>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <OptionButton
              onClick={handleDrawManually}
              endIcon={<ChevronRightIcon />}
              startIcon={<img src={manualDrawImage} alt="Manual draw" />}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: "black" }}
                >
                  {t("noFieldsModal.manual.draw.title")}
                </Typography>
                <OptionDescription variant="body2">
                  {t("noFieldsModal.manual.draw.description")}
                </OptionDescription>
              </Box>
            </OptionButton>
            <OptionButton
              onClick={handleContactSupport}
              endIcon={<ChevronRightIcon />}
              startIcon={
                <img src={contactSupportImage} alt="Contact support" />
              }
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: "black" }}
                >
                  {t("noFieldsModal.manual.support.title")} (support@ruumi.io)
                </Typography>
                <OptionDescription variant="body2">
                  {t("noFieldsModal.manual.support.description")}
                </OptionDescription>
              </Box>
            </OptionButton>
          </Box>
          <ActionButton
            variant="text"
            color="primary"
            onClick={() => setShowManualOptions(false)}
          >
            {t("noFieldsModal.back")}
          </ActionButton>
        </GreenCard>
      </DetailDialog>
    );
  }

  return (
    <DetailDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      headerActions={headerActions}
      contentSx={{ backgroundColor: "#F7FCF1" }}
      paperSx={
        isCompactDialog
          ? {
              height: { xs: "auto", sm: "auto" },
              maxHeight: {
                xs: "calc(100dvh - 70px - env(safe-area-inset-bottom))",
                sm: "90vh",
              },
              maxWidth: { xs: "100vw", sm: 640 },
              width: { xs: "100vw", sm: "min(640px, 90vw)" },
            }
          : undefined
      }
    >
      <GreenCard>
        {showPreviewActions && (
          <Box
            sx={{
              height: 0,
              overflow: "hidden",
              position: "absolute",
              width: 0,
            }}
          >
            <button
              type="button"
              data-action="dialog-back"
              onClick={() => setShowManualOptions(true)}
              disabled={isWorking}
              aria-label={t("noFieldsModal.manual.button")}
              tabIndex={-1}
            />
            <button
              type="button"
              data-action="dialog-save"
              onClick={handleImportWithSBI}
              disabled={isWorking}
              aria-label={t("noFieldsModal.import.buttonWithCount", {
                count: selectedFieldsCount,
              })}
              tabIndex={-1}
            />
          </Box>
        )}
        {step === "success" ? (
          <>
            <Typography variant="body1">
              {notImportedResultCount > 0
                ? t("noFieldsModal.success.summaryWithFailures", {
                    importedCount: fieldsCreated ?? importedResultCount,
                    notImportedCount: notImportedResultCount,
                  })
                : t("noFieldsModal.success.summaryAllImported", {
                    importedCount: fieldsCreated ?? importedResultCount,
                  })}
            </Typography>
            {renderImportResultList()}
            <Alert severity="info" sx={{ width: "100%", textAlign: "left" }}>
              {t("noFieldsModal.success.dryMatter")}
            </Alert>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={handleViewFields}
              sx={{ backgroundColor: "#2E7D32" }}
            >
              {t("noFieldsModal.success.button")}
            </ActionButton>
          </>
        ) : (
          <>
            {preview && step === "preview" ? (
              <></>
            ) : (
              <>
                {showSBIEntry && (
                  <>
                    <Typography variant="body1" gutterBottom>
                      {t("noFieldsModal.description")}
                    </Typography>
                    <SBILookupRow>
                      <DigitsContainer>
                        {digits.map((digit, index) => {
                          const InputComponent =
                            index === 0
                              ? FirstDigitInput
                              : index === digits.length - 1
                              ? LastDigitInput
                              : DigitInput;
                          return (
                            <InputComponent
                              key={index}
                              value={digit}
                              disabled={isWorking}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => handleDigitChange(index, e.target.value)}
                              onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>
                              ) => handleKeyDown(index, e)}
                              onPaste={handlePaste}
                              inputRef={(el: HTMLInputElement | null) =>
                                (inputRefs.current[index] = el)
                              }
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: 1,
                                "aria-label": `SBI digit ${index + 1}`,
                              }}
                            />
                          );
                        })}
                      </DigitsContainer>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCheckSBI}
                        disabled={!isSbiComplete || isWorking}
                        sx={{
                          backgroundColor: "#2E7D32",
                          borderRadius: 2,
                          minHeight: 48,
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("noFieldsModal.check.button")}
                      </Button>
                    </SBILookupRow>
                    {!isSbiComplete && sbiNumber.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t("noFieldsModal.sbi.validation")}
                      </Typography>
                    )}
                    {showSBIEntryError && (
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ textAlign: "center" }}
                      >
                        {error}
                      </Typography>
                    )}
                  </>
                )}
                {showEntryManualOptions && (
                  <Box
                    sx={{
                      borderTop: showSBIEntry
                        ? "1px solid rgba(46, 125, 50, 0.18)"
                        : "none",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      mt: showSBIEntry ? 1 : 0,
                      pt: showSBIEntry ? 2 : 0,
                      width: "100%",
                    }}
                  >
                    {showSBIEntry && (
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ textAlign: "left" }}
                      >
                        {t("noFieldsModal.manual.inlineTitle")}
                      </Typography>
                    )}
                    <OptionButton
                      onClick={handleDrawManually}
                      endIcon={<ChevronRightIcon />}
                      startIcon={<img src={manualDrawImage} alt="" />}
                    >
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          sx={{ color: "black" }}
                        >
                          {t("noFieldsModal.manual.draw.title")}
                        </Typography>
                        <OptionDescription variant="body2">
                          {t("noFieldsModal.manual.draw.description")}
                        </OptionDescription>
                      </Box>
                    </OptionButton>
                    <OptionButton
                      onClick={handleContactSupport}
                      endIcon={<ChevronRightIcon />}
                      startIcon={<img src={contactSupportImage} alt="" />}
                    >
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          sx={{ color: "black" }}
                        >
                          {t("noFieldsModal.manual.support.title")}{" "}
                          (support@ruumi.io)
                        </Typography>
                        <OptionDescription variant="body2">
                          {t("noFieldsModal.manual.support.description")}
                        </OptionDescription>
                      </Box>
                    </OptionButton>
                  </Box>
                )}
              </>
            )}
            {step === "checking" && (
              <Box sx={{ width: "100%", textAlign: "left" }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("noFieldsModal.progress.checking")}
                </Typography>
                <LinearProgress />
              </Box>
            )}
            {step === "importing" && (
              <Box
                sx={{
                  alignItems: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t("noFieldsModal.progress.importing")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("noFieldsModal.progress.importingDescription")}
                  </Typography>
                </Box>
                <LinearProgress />
                {renderImportResultList()}
              </Box>
            )}
            {preview && step === "preview" && (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1fr) 360px",
                  },
                  height: { md: "min(720px, calc(90vh - 128px))" },
                  minHeight: { md: 560 },
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.5,
                    gridTemplateRows: { md: "minmax(280px, 1fr) auto" },
                    minWidth: 0,
                  }}
                >
                  <SBIImportMapPreview
                    activeSourceIndex={activeSourceIndex}
                    fields={editableFields.map((field) => ({
                      sourceIndex: field.sourceIndex,
                      name: field.name,
                      include: field.include,
                      geometry: field.geometry,
                    }))}
                    onFieldClick={setActiveSourceIndex}
                  />

                  {activeField && (
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "white",
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: "left",
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          gap: 1,
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t("noFieldsModal.selection.selectedField")}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={activeField.include}
                              size="small"
                              onChange={(event) =>
                                updateEditableField(activeField.sourceIndex, {
                                  include: event.target.checked,
                                })
                              }
                            />
                          }
                          label={t("noFieldsModal.selection.include")}
                          sx={{ mr: 0 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gap: 1.25,
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(0, 1fr) minmax(150px, 0.55fr) minmax(170px, 0.7fr)",
                          },
                        }}
                      >
                        <TextField
                          label={t("noFieldsModal.selection.name")}
                          size="small"
                          value={activeField.name}
                          disabled={!activeField.include || isWorking}
                          error={
                            activeField.include && !activeField.name.trim()
                          }
                          onChange={(event) =>
                            updateEditableField(activeField.sourceIndex, {
                              name: event.target.value,
                            })
                          }
                        />
                        <TextField
                          select
                          label={t("fieldinput.fieldType.text")}
                          size="small"
                          value={activeField.fieldType}
                          disabled={!activeField.include || isWorking}
                          onChange={(event) =>
                            handleFieldTypeChange(
                              activeField.sourceIndex,
                              event.target.value as FIELD_TYPE
                            )
                          }
                        >
                          <MenuItem value={FIELD_TYPE.GRASS}>
                            {t("fieldinput.fieldType.option.GRASS")}
                          </MenuItem>
                          <MenuItem value={FIELD_TYPE.ARABLE}>
                            {t("fieldinput.fieldType.option.ARABLE")}
                          </MenuItem>
                        </TextField>
                        {activeField.fieldType === FIELD_TYPE.GRASS ? (
                          <TextField
                            select
                            label={t("fieldinput.grassType.text")}
                            size="small"
                            value={activeField.grassType}
                            disabled={!activeField.include || isWorking}
                            onChange={(event) =>
                              updateEditableField(activeField.sourceIndex, {
                                grassType: event.target.value as GRASS_TYPE,
                              })
                            }
                          >
                            {Object.values(GRASS_TYPE).map((grassType) => (
                              <MenuItem key={grassType} value={grassType}>
                                {t(`fieldinput.grassType.option.${grassType}`)}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            select
                            label={t("fieldinput.arableType.text")}
                            size="small"
                            value={activeField.arableType}
                            disabled={!activeField.include || isWorking}
                            onChange={(event) =>
                              updateEditableField(activeField.sourceIndex, {
                                arableType: event.target.value as ARABLE_TYPE,
                              })
                            }
                          >
                            {Object.values(ARABLE_TYPE).map((arableType) => (
                              <MenuItem key={arableType} value={arableType}>
                                {t(
                                  `fieldinput.arableType.option.${arableType}`
                                )}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    p: 1.5,
                    textAlign: "left",
                    maxHeight: { md: "100%" },
                    overflowX: "hidden",
                    overflowY: { xs: "visible", md: "hidden" },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderBottom: {
                        xs: "none",
                        md: "1px solid rgba(0, 0, 0, 0.08)",
                      },
                      mb: 2,
                      pb: 1.5,
                      pt: 0,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("noFieldsModal.selection.listTitle")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {t("noFieldsModal.preview.description")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 1.5,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAllFieldsIncluded(true)}
                      >
                        {t("noFieldsModal.selection.selectAll")}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAllFieldsIncluded(false)}
                      >
                        {t("noFieldsModal.selection.deselectAll")}
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: { md: 1 },
                      gap: 0.75,
                      minHeight: 0,
                      overflowY: { xs: "visible", md: "auto" },
                      pr: { md: 0.5 },
                    }}
                  >
                    {editableFields.map((field) => (
                      <Box
                        key={field.sourceIndex}
                        onClick={() => setActiveSourceIndex(field.sourceIndex)}
                        sx={{
                          border: "1px solid",
                          borderColor:
                            field.sourceIndex === activeSourceIndex
                              ? "#f6c343"
                              : "divider",
                          borderRadius: 1.5,
                          display: "grid",
                          gridTemplateColumns: "auto minmax(0, 1fr) auto",
                          gap: 1,
                          alignItems: "center",
                          p: 1,
                          backgroundColor: field.include ? "white" : "grey.50",
                          cursor: "pointer",
                        }}
                      >
                        <Checkbox
                          checked={field.include}
                          size="small"
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            updateEditableField(field.sourceIndex, {
                              include: event.target.checked,
                            });
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {field.name ||
                              t("noFieldsModal.selection.unnamedField")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            component="div"
                          >
                            {getFieldSubtypeLabel(field)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={getFieldKindLabel(field)}
                          color={field.include ? "success" : "default"}
                          variant={
                            field.sourceIndex === activeSourceIndex
                              ? "filled"
                              : "outlined"
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
            {preview && step === "preview" && !canImportSelectedFields && (
              <Typography variant="body2" color="error" align="center">
                {t("noFieldsModal.selection.error")}
              </Typography>
            )}
          </>
        )}
        {error && !showSBIEntryError && (
          <Typography color="error" variant="body2" gutterBottom align="center">
            {error}
          </Typography>
        )}
      </GreenCard>
    </DetailDialog>
  );
}
